package fyi.moko.hotpl.data.network

import kotlinx.serialization.Serializable

// --- Places API (New) autocomplete request (POST /v1/places:autocomplete) ---

@Serializable
data class AutocompleteRequest(
    val input: String,
    val includedPrimaryTypes: List<String> = emptyList(),
    val locationBias: LocationBias? = null,
    /**
     * The point distances are measured from. Supplying it is what makes the API populate
     * [PlacePrediction.distanceMeters]; without it every prediction comes back distance-less.
     */
    val origin: LatLng? = null,
    val languageCode: String = SEARCH_LANGUAGE_CODE,
)

@Serializable
data class LocationBias(
    val circle: Circle,
)

@Serializable
data class Circle(
    val center: LatLng,
    /** Meters; the API rejects values above 50,000. */
    val radius: Double,
)

@Serializable
data class LatLng(
    val latitude: Double,
    val longitude: Double,
)

// --- Places API (New) distance-ranked search (POST /v1/places:searchText | :searchNearby) ---

/**
 * Text search ranked by distance. Unlike autocomplete this is a real proximity query:
 * `rankPreference = DISTANCE` orders by how far each result is from the bias circle's
 * centre, so the nearest branch of a chain cannot be ranked away by prominence.
 * The API requires a locationBias (or restriction) for DISTANCE ranking to be accepted.
 */
@Serializable
data class SearchTextRequest(
    val textQuery: String,
    val locationBias: LocationBias,
    val rankPreference: String = RANK_BY_DISTANCE,
    val maxResultCount: Int = MAX_SEARCH_RESULTS,
    val languageCode: String = SEARCH_LANGUAGE_CODE,
)

/** Query-less "what is around me" search; the circle is a hard restriction, not a hint. */
@Serializable
data class SearchNearbyRequest(
    val locationRestriction: LocationRestriction,
    val includedTypes: List<String> = emptyList(),
    val rankPreference: String = RANK_BY_DISTANCE,
    val maxResultCount: Int = MAX_SEARCH_RESULTS,
    val languageCode: String = SEARCH_LANGUAGE_CODE,
)

@Serializable
data class LocationRestriction(
    val circle: Circle,
)

@Serializable
data class PlacesSearchResponse(
    val places: List<SearchResultPlace> = emptyList(),
)

@Serializable
data class SearchResultPlace(
    val id: String,
    val displayName: LocalizedText,
    val location: LatLng,
    val formattedAddress: String? = null,
)

const val RANK_BY_DISTANCE = "DISTANCE"
const val MAX_SEARCH_RESULTS = 10

/**
 * Every search result is asked for in English, whatever the device's locale is.
 *
 * Both providers otherwise answer in the language the HTTP client advertises, and a
 * Korean-locale phone was getting rows like "스타벅스" and addresses ending in "미국" mixed
 * in with English ones — this app's readers are English speakers. Only the language is
 * pinned, not a region: a regionCode would also bias *which* places come back, which is a
 * different decision and not one the reader's language should make.
 */
const val SEARCH_LANGUAGE_CODE = "en"

// --- Places API (New) autocomplete response ---

/** Top-level response from the Places API (New) autocomplete endpoint. */
@Serializable
data class AutocompleteResponse(
    val suggestions: List<AutocompleteSuggestion> = emptyList(),
)

/** A suggestion wrapper; query predictions carry no [placePrediction] and are skipped. */
@Serializable
data class AutocompleteSuggestion(
    val placePrediction: PlacePrediction? = null,
)

@Serializable
data class PlacePrediction(
    val placeId: String,
    val structuredFormat: StructuredFormat,
    /** Straight-line metres from the request's `origin`; absent when no origin was sent. */
    val distanceMeters: Int? = null,
)

/** The user-visible text split into a primary name and optional secondary line. */
@Serializable
data class StructuredFormat(
    val mainText: LocalizedText,
    val secondaryText: LocalizedText? = null,
)

@Serializable
data class LocalizedText(
    val text: String,
)

// --- Places API (New) place details response (GET /v1/places/{placeId}) ---

/** The fields requested via `X-Goog-FieldMask` from the Place Details endpoint. */
@Serializable
data class PlaceDetailsResponse(
    val displayName: LocalizedText,
    val location: LatLng,
    val nationalPhoneNumber: String? = null,
    val websiteUri: String? = null,
    /** Google's own place-type vocabulary, mapped to tags by [googleTypeTags]. */
    val types: List<String> = emptyList(),
    val primaryType: String? = null,
)
